import React from "react";
import LegalPageLayout, { Section, Callout, CrossDocLink } from "./legal/LegalPageLayout";

const SECTIONS = [
  { id: "acceptance", number: 1, title: "Acceptance of these terms" },
  { id: "who-can-use", number: 2, title: "Who can use LearnAndMasterAI" },
  { id: "accounts", number: 3, title: "Accounts & invitations" },
  { id: "acceptable-use", number: 4, title: "Acceptable use" },
  { id: "monitoring", number: 5, title: "Content monitoring & safety review" },
  { id: "content-ownership", number: 6, title: "Your content" },
  { id: "ai-disclaimer", number: 7, title: "AI output isn't always right" },
  { id: "availability", number: 8, title: "Availability & changes to the service" },
  { id: "suspension", number: 9, title: "Suspension & termination" },
  { id: "changes-to-terms", number: 10, title: "Changes to these terms" },
  { id: "contact", number: 11, title: "Contact us" },
];

export default function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="August 13, 2026"
      intro="These terms cover your use of LearnAndMasterAI — the AI tutoring chat, curriculum tools, and dashboards provided to you through your school. By using the platform, you're agreeing to them."
      sections={SECTIONS}
      crossLinkTo="/privacy"
      crossLinkLabel="Privacy Policy"
    >
      <Section id="acceptance" number={1} title="Acceptance of these terms">
        <p>
          By signing in to or otherwise using LearnAndMasterAI, you agree to these Terms of Service
          and to our{" "}
          <CrossDocLink to="/privacy">Privacy Policy</CrossDocLink>
          . If you're a student under the age required to accept terms on your own in your
          jurisdiction, your school has authorized your use of the platform on your behalf — see{" "}
          <strong>Section 2</strong>.
        </p>
      </Section>

      <Section id="who-can-use" number={2} title="Who can use LearnAndMasterAI">
        <p>
          LearnAndMasterAI is provided to schools, which invite their own students, teachers, and
          administrators. There is no public self-registration: every account is created through a
          school-issued invitation, and access can be granted or revoked by the school at any time.
        </p>
        <p>Accounts fall into a small number of roles, each with different capabilities:</p>
        <ul>
          <li><strong>Students</strong> use the tutoring chat and, where enabled, curriculum tools.</li>
          <li><strong>Teachers</strong> have class-scoped visibility into their students' activity.</li>
          <li>
            <strong>School administrators</strong> manage accounts, invitations, and safety review for
            their school.
          </li>
        </ul>
      </Section>

      <Section id="accounts" number={3} title="Accounts & invitations">
        <p>
          You're responsible for keeping your password confidential and for activity that happens
          under your account. If you think your account has been accessed without your permission,
          tell your school administrator or contact us right away. Your school controls whether your
          account remains active — access can be time-limited, paused, or revoked by your school
          without notice from us.
        </p>
      </Section>

      <Section id="acceptable-use" number={4} title="Acceptable use">
        <p>You agree to use LearnAndMasterAI the way it's intended: as a study and tutoring tool. That means not using it to:</p>
        <ul>
          <li>Submit someone else's schoolwork as your own, or otherwise misrepresent AI-assisted work as fully your own where your school's academic-integrity policy requires disclosure.</li>
          <li>Harass, bully, or threaten another person.</li>
          <li>Attempt to bypass safety monitoring, disable safety flags, or probe the platform for security vulnerabilities without authorization.</li>
          <li>Upload content you don't have the right to share, or that violates your school's code of conduct.</li>
        </ul>
        <p>
          Your school's own code of conduct and acceptable-use policy also apply to how you use
          LearnAndMasterAI, and your school can act on violations independently of us.
        </p>
      </Section>

      <Section id="monitoring" number={5} title="Content monitoring & safety review">
        <Callout>
          Conversations on this platform are monitored for student safety, and chat content —
          including chats you delete from your own view — remains accessible to authorized school
          staff for safety review. Don't treat anything you send through the chat as private from
          your school.
        </Callout>
        <p>
          LearnAndMasterAI automatically screens chat messages for indicators of risk (for example,
          references to self-harm or safety threats) and routes flagged conversations to authorized
          staff at your school. Deleting a chat removes it from your own history but does not remove
          it from the record available to staff conducting safety review — see the Privacy Policy's{" "}
          <CrossDocLink to="/privacy#chat-retention">
            Chat content, deletion &amp; staff review
          </CrossDocLink>{" "}
          section for the full explanation.
        </p>
      </Section>

      <Section id="content-ownership" number={6} title="Your content">
        <p>
          You (or, for younger students, your school on your behalf) retain ownership of the messages
          and files you submit through the chat. By using the platform, you grant your school and
          LearnAndMasterAI the limited right to store, process, and display that content as needed to
          provide the service — including generating a tutor response, applying safety monitoring,
          and retaining it for the staff-review purposes described above.
        </p>
      </Section>

      <Section id="ai-disclaimer" number={7} title="AI output isn't always right">
        <p>
          The tutoring chat is powered by an AI model. It's built and guided to be helpful for
          schoolwork, but like any AI system it can be confidently wrong, out of date, or miss
          context your teacher has. Treat its answers as a starting point for learning, not as a
          substitute for instruction from your teacher or as a final authority on a graded answer.
        </p>
      </Section>

      <Section id="availability" number={8} title="Availability & changes to the service">
        <p>
          We aim to keep LearnAndMasterAI available and working well, but we don't guarantee
          uninterrupted access — we may need to take the service down for maintenance, or change,
          add, or remove features as the product evolves.
        </p>
      </Section>

      <Section id="suspension" number={9} title="Suspension & termination">
        <p>
          Your school can revoke or pause your access at any time, for any reason consistent with its
          own policies. We may also suspend an account that we determine, in good faith, is being used
          to violate these terms, poses a safety risk, or compromises the security of the platform —
          and where practical, we'll notify the relevant school when we do.
        </p>
      </Section>

      <Section id="changes-to-terms" number={10} title="Changes to these terms">
        <p>
          We may update these terms as the product changes. If we make a material change, we'll update
          the "Last updated" date above and notify schools so they can pass relevant changes along to
          students and families as needed. Continuing to use LearnAndMasterAI after an update means
          you accept the revised terms.
        </p>
      </Section>

      <Section id="contact" number={11} title="Contact us">
        <p>
          Questions about these terms can be sent to{" "}
          <strong>
            <a href="mailto:info@learnandmasterai.com">
              info@learnandmasterai.com
            </a>
          </strong>
          .
        </p>
      </Section>
    </LegalPageLayout>
  );
}
